import * as React from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "convex/react";
import MarkdownPreview from "@uiw/react-markdown-preview";
import "@uiw/react-markdown-preview/markdown.css";
import { useTheme } from "next-themes";
import { ChevronRight, FileText, Home } from "lucide-react";
import { api } from "$convex/convexApi";
import { Badge } from "@/components/ui/badge";
import { useAuthIdentity } from "@/contexts/UserProfileContext";
import { formatDate } from "@/lib/format";

const markdownComponents: NonNullable<React.ComponentProps<typeof MarkdownPreview>["components"]> = {
  a: ({ node: _node, ...props }: React.ComponentPropsWithoutRef<"a"> & { node?: unknown }) => (
    <a {...props} target="_blank" rel="noopener noreferrer" />
  ),
};

export default function DocsViewerPage() {
  const { slug } = useParams<{ slug: string }>();
  const { authUserId } = useAuthIdentity();
  const { resolvedTheme } = useTheme();

  const doc = useQuery(
    api.knowledgeBase.getDocumentBySlug,
    authUserId && slug
      ? { authUserId, slug, publishedOnly: true }
      : "skip",
  );

  if (!slug) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-24 text-center">
        <FileText className="h-10 w-10 text-muted-foreground" />
        <p className="text-muted-foreground">
          Open a document from a shared link to view it here.
        </p>
      </div>
    );
  }

  if (doc === undefined) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="text-muted-foreground">Loading document...</div>
      </div>
    );
  }

  if (doc === null) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-24 text-center">
        <FileText className="h-10 w-10 text-muted-foreground" />
        <p className="text-lg font-medium">Document not found</p>
        <p className="text-sm text-muted-foreground">
          This document may not exist or is not yet published.
        </p>
      </div>
    );
  }

  const folderSegments = doc.folderPath
    ? doc.folderPath.split("/").filter(Boolean)
    : [];

  return (
    <article className="mx-auto max-w-4xl space-y-6">
      {/* Breadcrumb */}
      {folderSegments.length > 0 && (
        <nav className="flex items-center gap-1 text-sm text-muted-foreground">
          <Home className="h-3.5 w-3.5" />
          {folderSegments.map((segment: string, i: number) => (
            <React.Fragment key={i}>
              <ChevronRight className="h-3.5 w-3.5" />
              <span>{segment}</span>
            </React.Fragment>
          ))}
        </nav>
      )}

      {/* Header */}
      <header className="space-y-3">
        <h1 className="text-3xl font-bold tracking-tight">{doc.title}</h1>

        {doc.summary && (
          <p className="text-lg text-muted-foreground">{doc.summary}</p>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary">{doc.category}</Badge>
          {doc.tags?.map((tag: string) => (
            <Badge key={tag} variant="outline">
              {tag}
            </Badge>
          ))}
          <span className="text-xs text-muted-foreground">
            Updated {formatDate(doc.updatedAt)}
          </span>
        </div>
      </header>

      {/* Markdown content */}
      <div
        data-color-mode={resolvedTheme === "dark" ? "dark" : "light"}
        className="rounded-lg border p-6"
      >
        <MarkdownPreview
          source={doc.contentMarkdown}
          components={markdownComponents}
          style={{ backgroundColor: "transparent" }}
        />
      </div>
    </article>
  );
}
